import { Media } from '../models/media';
import { DatabaseModel } from './database-model';
import { Language } from './language';
import { PurchaseWebhook, PurchaseWebhookType } from './purchase-webhook';

export enum ProductType {
    Digital = "Digital",
    Reward = "Reward",
    Ticket = "Ticket",
    Tour = "Tour",
    Food = "Food"
}

export enum ProductTag {
    podcast = 'podcast',    // matches an svg icon
    meal = 'meal',          // matches an svg icon
    drink = 'drink',        // matches an svg icon
    admission = 'admission' // matches an svg icon
}

export enum PartnerCostType {
    FIXED = 'FIXED',
    PERCENTAGE = 'PERCENTAGE'
}

export enum PartnerCostTransferType {
    StripeTransfer = 'StripeTransfer',
    Invoice = 'Invoice'
}

export class Product extends DatabaseModel {
    public key: string = "";
    // Random translation key (uuid) which groups product translations
    public translationKey: string = "";
    // Language of the product
    public language: Language = null;
    // Title of the product shown to the user
    public title: string = "";
    // Describes the product and what it includes
    public text: string = "";
    // Describes in short comma separated format what is included in the product
    public shortText: string = "";
    // Additional images or other media discribing the product
    public mediaArray: Media[] = [new Media()];
    // The main image of the product (a shorthand reference to the first media element)
    public image: Media = this.mediaArray[0];
    // Price of the product (in kr.)
    public price: number = null;
    public currency: string = "DKK";
    // Reference to stripe price (which should always point to the same price as the 'price') 
    public stripePriceKey: string = "";
    // Reference to stripe product ID
    public stripeId: string = "";
    // Product type
    public type: ProductType = ProductType.Reward;
    // Dynamically set byt the list that contains the product
    public order: number = 0;
    // Number of products available
    public stock: number = null;
    // Automatically refill the stock
    public autoStock: number = null;
    // Optional url to a external voucher overview page
    public voucherUrl: string = "";
    // SendInBlue mail template ID
    public mailTemplateId: number = null;
    // Optional disclaimer shown to user be purchasing the product
    public disclaimer: string = "";
    // Project key
    public project: string = "";
    // Reference to tour that the product gives access to in case it is a Tour / FoodTour
    public tourKey: string = "";
    // Firebase time stamp object on or timestamp string
    public createdOn: any = "";
    // Key of use created product
    public createdBy: string = "";
    // Partner ID (connect accounts in Stripe)
    public partnerId: string = "";
    // Partner cost / "indkøbspris" (in øre or percentage)
    public partnerCost: number = null;
    // Partner cost type (fixed cost or a percentage of the product price)
    public partnerCostType: PartnerCostType = null;
    // Parent product key
    public parent: string = "";
    // Venue name (if product includes items to redeem at a venue)
    public redeemPlaces: string = "";
    // Links to the redeem place websites where users can look up opening hours and booking
    public redeemWebsites: string = "";
    // Tags
    public tags: string = "";
    // Webhook to call when a product has been purchased in order to create the voucher (ie. get a ticket barcode)
    public purchaseWebhook: PurchaseWebhook = {
        webhookType: PurchaseWebhookType.Disabled
    };

    constructor(key: string = null, json: any = null, order: number = 0) {
        super();
        this.key = key;
        this.order = order;

        if(json) {
            this.fromJson(json);
        }
        // Automatically generate a new key if non given and new product was created
        if(!this.key) this.key = this.genKey();
    }

    public fromJson(json: any): void {
        this.language = json.language || null;
        this.translationKey = json.translationKey || '';
        this.title = json.title || "New Product";
        this.text = json.text || "";
        this.shortText = json.shortText || "";
        this.price = Product.parseNumber(json.price) || null;
        this.stripePriceKey = json.stripePriceKey || '';
        this.stripeId = json.stripeId || '';
        this.currency = json.currency || "DKK";
        this.type = json.type || ProductType.Reward;
        this.project = json.project || "";
        this.tourKey = json.tourKey || "";
        this.createdOn = json.createdOn || "";
        this.createdBy = json.createdBy || "";
        this.voucherUrl = json.voucherUrl || "";
        this.mailTemplateId = Product.parseNumber(json.mailTemplateId) || null;
        this.disclaimer = json.disclaimer || "";
        this.stock = Product.parseNumber(json.stock) || null;
        this.autoStock = Product.parseNumber(json.autoStock) || null;
        this.partnerId = json.partnerId || "";
        this.partnerCost = Product.parseNumber(json.partnerCost) || null;
        this.partnerCostType = json.partnerCostType || PartnerCostType.FIXED;
        this.redeemWebsites = json.redeemWebsites || "";
        this.parent = json.parent || "";
        this.tags = json.tags || "";
        this.redeemPlaces = json.redeemPlaces || "";

        this.purchaseWebhook = {
            webhookType: json.purchaseWebhook?.webhookType || PurchaseWebhookType.Disabled,
            webhookUrl: json.purchaseWebhook?.webhookUrl || '',
            partnerProductId: json.purchaseWebhook?.partnerProductId || '',
        }

        // Init media array with new objects
        let images = json.mediaArray || [];
        // Convert map object to array - it seems Firebase now always returns a map and not array (even if the index is a number)
        images =  (typeof images == 'object') ? DatabaseModel.mapToArray(images) : images || [];

        this.mediaArray = images && images.length ? images.map((jsonImage) => new Media(jsonImage, jsonImage.key)) : [];
        // Set product image to first element of media array from database
        if(this.mediaArray[0]) this.image = this.mediaArray[0];
    }

    public toJson() {
        // Add image to the media array (if a new one was added by user)
        if(this.image && this.mediaArray.length <= 0) {
            this.mediaArray.push(this.image);
        }

        return {
            language: this.language,
            translationKey: this.translationKey,
            title: this.title,
            text: this.text,
            shortText: this.shortText,
            price: this.price,
            stripePriceKey: this.stripePriceKey,
            stripeId: this.stripeId,
            currency: this.currency,
            type: this.type,
            project: this.project,
            tourKey: this.tourKey,
            createdOn: this.createdOn,
            createdBy: this.createdBy,
            voucherUrl: this.voucherUrl,
            mailTemplateId: this.mailTemplateId,
            disclaimer: this.disclaimer,
            stock: this.stock,
            autoStock: this.autoStock,
            mediaArray: this.mediaArray.map(media => media.toJson()),
            partnerId: this.partnerId,
            partnerCost: this.partnerCost,
            partnerCostType: this.partnerCostType,
            redeemWebsites: this.redeemWebsites,
            parent: this.parent,
            tags: this.tags,
            redeemPlaces: this.redeemPlaces,
            purchaseWebhook: this.purchaseWebhook,
        };
    }

    private static parseNumber(num: number | string): number {
        if(typeof num == 'string') return parseInt(num);
        else return num;
    }
}
